import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { TulsiLogo } from "@/lib/brand";
import { saveLogin, loadLogin, clearSavedLogin, savedLoginSupported } from "@/lib/savedLogin";

// Last ID used on this device (never the password) so it need not be retyped.
const LAST_KEY = "tulsi_last_login";
const REMEMBER_KEY = "tulsi_keep_signed_in";
function readLast() {
  try { return JSON.parse(localStorage.getItem(LAST_KEY) || "null"); } catch { return null; }
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const last = readLast();
  const [loginType, setLoginType] = useState(last?.type === "phone" ? "phone" : "email");
  const [identifier, setIdentifier] = useState(last?.identifier || "");
  // Remember the password itself on this device (opt-in, on by default).
  const REMEMBER_PW_KEY = "tulsi_remember_password";
  const [rememberPw, setRememberPw] = useState(() => {
    try { return localStorage.getItem("tulsi_remember_password") !== "0"; } catch { return true; }
  });
  const [rememberMe, setRememberMe] = useState(() => {
    try { return localStorage.getItem(REMEMBER_KEY) !== "0"; } catch { return true; }
  });
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Fill from the login saved on this device (see lib/savedLogin.js).
  useEffect(() => {
    if (user) return;
    let cancelled = false;
    loadLogin().then((c) => {
      if (cancelled || !c) return;
      setLoginType(c.type === "email" ? "email" : "phone");
      setIdentifier(c.identifier || "");
      setPassword(c.password || "");
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ask the browser (Chrome's password manager) for the saved login and fill
  // it in — a single-page app login never triggers the browser's own autofill
  // reliably. The password stays in the browser's encrypted store; the app
  // never keeps it.
  useEffect(() => {
    if (user || !window.PasswordCredential || !navigator.credentials?.get) return;
    let cancelled = false;
    navigator.credentials.get({ password: true, mediation: "optional" })
      .then((cred) => {
        if (cancelled || !cred || cred.type !== "password") return;
        const isEmail = cred.id.includes("@");
        setLoginType(isEmail ? "email" : "phone");
        setIdentifier(isEmail ? cred.id : cred.id.replace(/\D/g, "").slice(-10));
        setPassword(cred.password || "");
      })
      .catch(() => { /* no saved login or user dismissed the chooser */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      if (user.must_change_password) navigate("/change-password", { replace: true });
      else navigate(location.state?.from || "/", { replace: true });
    }
  }, [user, navigate, location.state]);

  // Phone login always matches the backend's stored E.164 number, so the
  // "+91" has to be there on submit — but typing/seeing it every time on a
  // 10-digit number that never changes is just friction. Show it as a fixed
  // prefix instead, and only ask the user to type the digits.
  const fullIdentifier = () => {
    if (loginType !== "phone") return identifier.trim();
    return `+91${identifier.replace(/\D/g, "")}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = await login({ identifier: fullIdentifier(), password, loginType, rememberMe });
      if (rememberPw) {
        await saveLogin({ type: loginType, identifier: identifier.trim(), password });
      } else {
        await clearSavedLogin();
      }
      try {
        localStorage.setItem(REMEMBER_PW_KEY, rememberPw ? "1" : "0");
        if (window.PasswordCredential && navigator.credentials?.store) {
          await navigator.credentials.store(new window.PasswordCredential({
            id: loginType === "phone" ? identifier.replace(/\D/g, "") : identifier.trim(),
            password,
            name: data.user.name,
          }));
        }
      } catch { /* browser declined to save */ }
      try {
        localStorage.setItem(LAST_KEY, JSON.stringify({ type: loginType, identifier: identifier.trim() }));
        localStorage.setItem(REMEMBER_KEY, rememberMe ? "1" : "0");
      } catch { /* storage blocked */ }
      toast.success(`Welcome, ${data.user.name}`);
      if (data.must_change_password) navigate("/change-password", { replace: true });
      else navigate("/", { replace: true });
    } catch (err) {
      // A saved password that no longer works (changed elsewhere) must not
      // keep being filled in.
      if (err?.response?.status === 401) { clearSavedLogin(); }
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen w-full flex items-center justify-center px-4 py-10"
      style={{
        backgroundImage:
          "radial-gradient(1200px 600px at 10% -10%, rgba(229,25,127,0.14), transparent), radial-gradient(1000px 600px at 100% 110%, rgba(109,44,145,0.14), transparent)",
      }}
    >
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <TulsiLogo size={56} />
        </div>

        <div className="bg-white border border-neutral-200 shadow-xl shadow-[#6D2C91]/5 rounded-2xl p-6 sm:p-8">
          <div className="mb-6">
            <h1 className="text-2xl font-bold tracking-tight text-neutral-900">Sign in</h1>
            <p className="text-sm text-neutral-500 mt-1">
              Choose your access channel below.
            </p>
          </div>

          <Tabs
            value={loginType}
            onValueChange={(v) => {
              setLoginType(v);
              setIdentifier(readLast()?.type === v ? (readLast()?.identifier || "") : "");
              setError("");
            }}
            className="mb-5"
          >
            <TabsList className="grid grid-cols-2 w-full h-11 rounded-xl bg-neutral-100 p-1">
              <TabsTrigger
                value="email"
                data-testid="login-tab-office"
                className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-[#6D2C91] font-medium"
              >
                <Mail className="h-4 w-4 mr-2" /> Office
              </TabsTrigger>
              <TabsTrigger
                value="phone"
                data-testid="login-tab-field"
                className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-[#6D2C91] font-medium"
              >
                <Phone className="h-4 w-4 mr-2" /> Field / Distributor
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div>
              <Label htmlFor="identifier" className="text-sm font-medium text-neutral-700">
                {loginType === "email" ? "Work email" : "Phone number"}
              </Label>
              {loginType === "phone" ? (
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-500 select-none">
                    +91
                  </span>
                  <Input
                    id="identifier"
                    data-testid="login-identifier-input"
                    type="tel"
                    inputMode="tel"
                    placeholder="90000 00001"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    name="username"
                    autoComplete="username"
                    maxLength={10}
                    required
                    className="h-11 pl-10"
                  />
                </div>
              ) : (
                <Input
                  id="identifier"
                  data-testid="login-identifier-input"
                  type="email"
                  inputMode="email"
                  placeholder="you@tulsitea.com"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  name="username"
                  autoComplete="username"
                  required
                  className="mt-1 h-11"
                />
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-neutral-700">
                Password
              </Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  name="password"
                  autoComplete="current-password"
                  required
                  className="h-11 pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  data-testid="login-password-toggle"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center rounded-md text-neutral-500 hover:text-[#6D2C91] hover:bg-neutral-100"
                  aria-label={show ? "Hide password" : "Show password"}
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {savedLoginSupported() && (
              <label className="flex items-center gap-2 text-sm text-neutral-600 select-none cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberPw}
                  onChange={(e) => setRememberPw(e.target.checked)}
                  data-testid="login-remember-password-checkbox"
                  className="h-4 w-4 accent-[#6D2C91]"
                />
                Remember my password on this device
              </label>
            )}

            <label className="flex items-center gap-2 text-sm text-neutral-600 select-none cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                data-testid="login-remember-checkbox"
                className="h-4 w-4 accent-[#6D2C91]"
              />
              Keep me signed in on this device
            </label>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={busy || !identifier || !password}
              className="w-full h-11 tulsi-gradient text-white hover:opacity-95 font-semibold rounded-xl shadow-md shadow-[#E5197F]/20 disabled:opacity-50"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>

          <p className="text-xs text-neutral-400 text-center mt-6 leading-relaxed">
            By continuing you agree to Tulsi Tea's internal usage policy.
          </p>
        </div>

        <p className="text-center text-xs text-neutral-500 mt-6">
          v0.2 · Phase 2 · Web PWA
        </p>
      </div>
    </div>
  );
}
